<script>
import FileStatusBadge from '@/Pages/EmployeesImport/components/FileStatusBadge'
import { getEmployeeImportFileStatus } from '@/utils/employee-import'

export default {
    components: { FileStatusBadge },
    comments: {
        FileStatusBadge,
    },
    props: {
        companyUuid: String,
        employeeImport: Object,
        employeeImportFile: Object,
        isLatestFile: Boolean,
        isAuthorizedFromScp: Boolean,
        uploadFeatureEnabled: Boolean,
    },

    methods: {
        formatDate(date) {
            if (!date) {
                return ''
            }

            const options = {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            };

            const dateTime = new Date(date)
            return dateTime.toLocaleString({}, options)
        },
        uploadNewFile() {
            this.$inertia.get(route('nl.employerdashboard.employees-import.uploads.reupload', {
                employeeImport: this.employeeImport.uuid,
                company_as_uuid: this.$page.props.auth.user.selected_company.uuid
            }))
        }
    },

    computed: {
        currentFileStatus() {
            return getEmployeeImportFileStatus(this.employeeImportFile)
        },
        isProcessed() {
            return !!this.employeeImportFile.company_file?.core_processed_at
        },
    },
}
</script>

<template>
    <div class="card-body import-details-container pl-0">
        <div v-if="isLatestFile && currentFileStatus === 'validation_failed' && (uploadFeatureEnabled || isAuthorizedFromScp)"
             class="pb-4 font-poppins font-weight-light border-bottom" role="alert">
            <div class="card py-2 px-2 border-danger border-radius-8 ">
                <h4 class="bg-danger color-white p-1 border-radius-8">
                    <i class="fa fa-exclamation-circle p-2"></i>
                    {{
                        Lang.get('employerdashboard::pages/employees-import/show.validation_errors_in_latest.title')
                    }}
                </h4>

                <div class="pt-2 pl-2 pb-2">
                    <p class="font-weight-semibold color-dark-blue">
                        {{
                            Lang.get('employerdashboard::pages/employees-import/show.validation_errors_in_latest.cta')
                        }}<br>
                        <span class="font-weight-lighter"
                              v-html="Lang.get('employerdashboard::pages/employees-import/show.validation_errors_in_latest.description')"></span>
                    </p>
                    <button
                        class="btn btn-primary btn-orange font-poppins font-weight-semibold color-white border-radius-19 px-5"
                        @click="uploadNewFile">
                        {{
                            Lang.get('employerdashboard::pages/employees-import/show.validation_errors_in_latest.button')
                        }}
                    </button>
                </div>
            </div>
        </div>

        <div class="mb-4 font-poppins font-weight-light" role="alert"
             v-if="isLatestFile && currentFileStatus === 'waiting_for_approval'">
            <div class="bg-info-light color-dark-blue py-2 pl-2">
                <h4>
                    <i class="fa fa-exclamation-circle pr-2"></i>
                    {{ Lang.get('employerdashboard::pages/employees-import/show.waiting_for_scp_approval.title') }}
                </h4>

                <p class="pt-2 pl-4 font-weight-lighter"
                   v-html="Lang.get('employerdashboard::pages/employees-import/show.waiting_for_scp_approval.description')"></p>

                <inertia-link
                    :href="route('nl.employerdashboard.employees-import.file.assessment.approve', {
                            company_as_uuid: companyUuid,
                            employeeImport: employeeImport.uuid,
                            employeeImportFile: employeeImportFile.uuid,
                        })"
                    method="post"
                    as="button"
                    class="btn btn-primary btn-orange font-poppins font-weight-semibold color-white border-radius-19 ml-4 px-5"
                    v-if="isAuthorizedFromScp"
                >
                    {{
                        Lang.get('employerdashboard::pages/employees-import/show.waiting_for_scp_approval.buttons.approve_and_process')
                    }}
                </inertia-link>
            </div>
        </div>

        <dl class="mb-4">
            <div class="py-2 row ml-0 font-poppins color-dark-blue border-bottom">
                <dt class="col-12 col-md-4 font-weight-semibold">
                    {{ Lang.get('employerdashboard::pages/employees-import/show.file_details.original_file') }}
                </dt>

                <dd class="col-12 col-md-8 font-weight-lighter m-0">
                    <p class="m-0">{{ employeeImportFile.original_filename }}</p>
                </dd>
            </div>

            <div class="py-2 row ml-0 font-poppins color-dark-blue border-bottom">
                <dt class="col-12 col-md-4 font-weight-semibold">
                    {{ Lang.get('employerdashboard::pages/employees-import/show.file_details.status') }}
                </dt>

                <dd class="col-12 col-md-8 font-weight-lighter m-0">
                    <p class="m-0">
                        <file-status-badge :employee-import-file="employeeImportFile"
                                           class="px-5"></file-status-badge>
                    </p>
                </dd>
            </div>

            <div class="py-2 row ml-0 font-poppins color-dark-blue border-bottom">
                <dt class="col-12 col-md-4 font-weight-semibold">
                    {{ Lang.get('employerdashboard::pages/employees-import/show.file_details.employees_count') }}
                </dt>

                <dd class="col-12 col-md-8 font-weight-lighter m-0">
                    <p class="m-0">
                        <span v-if="employeeImportFile.processable_employees_count !== null || employeeImportFile.unprocessable_employees_count !== null">
                            {{ employeeImportFile.processable_employees_count + employeeImportFile.unprocessable_employees_count }}
                        </span>
                        <span v-if="employeeImportFile.unprocessable_employees_count">
                            {{
                                Lang.get('employerdashboard::pages/employees-import/show.file_details.employees_count_unprocessable', {
                                    count: employeeImportFile.unprocessable_employees_count,
                                })
                            }}
                        </span>
                    </p>
                </dd>
            </div>

            <div class="py-2 row ml-0 font-poppins color-dark-blue border-bottom">
                <dt class="col-12 col-md-4 font-weight-semibold">
                    {{ Lang.get('employerdashboard::pages/employees-import/show.file_details.created_at') }}
                </dt>

                <dd class="col-12 col-md-8 font-weight-lighter m-0">
                    <p class="m-0">{{ formatDate(employeeImportFile.created_at) }}</p>
                </dd>
            </div>

            <div class="py-2 row ml-0 font-poppins color-dark-blue border-bottom">
                <dt class="col-12 col-md-4 font-weight-semibold">
                    {{ Lang.get('employerdashboard::pages/employees-import/show.file_details.created_by') }}
                </dt>

                <dd class="col-12 col-md-8 font-weight-lighter m-0">

                    <p class="m-0" v-if="employeeImportFile.is_created_by_scp_user">
                        {{ Lang.get('employerdashboard::pages/employees-import/show.by.scp_user') }}
                    </p>
                    <p class="m-0" v-else>
                        {{ employeeImportFile.created_by_company_representative?.name }}
                    </p>
                </dd>
            </div>

            <div class="py-2 row ml-0 font-poppins color-dark-blue border-bottom">
                <dt class="col-12 col-md-4 font-weight-semibold">
                    {{ Lang.get('employerdashboard::pages/employees-import/show.file_details.approved_at') }}
                </dt>

                <dd class="col-12 col-md-8 font-weight-lighter m-0">
                    <p class="m-0">
                        <template v-if="employeeImportFile.is_approved_by_scp_user">
                            {{ formatDate(employeeImportFile.scp_user_approved_at) }}
                        </template>
                        <template v-else>
                            {{ formatDate(employeeImportFile.company_representative_approved_at) }}
                        </template>
                    </p>
                </dd>
            </div>

            <div class="py-2 row ml-0 font-poppins color-dark-blue border-bottom">
                <dt class="col-12 col-md-4 font-weight-semibold">
                    {{ Lang.get('employerdashboard::pages/employees-import/show.file_details.approved_by') }}
                </dt>

                <dd class="col-12 col-md-8 font-weight-lighter m-0">
                    <p class="m-0" v-if="employeeImportFile.is_approved_by_scp_user">
                        {{ Lang.get('employerdashboard::pages/employees-import/show.by.scp_user') }}
                    </p>
                    <p class="m-0" v-else>
                        {{ employeeImportFile.approved_by_company_representative?.name }}
                    </p>
                </dd>
            </div>

            <div class="py-2 row ml-0 font-poppins color-dark-blue border-bottom">
                <dt class="col-12 col-md-4 font-weight-semibold">
                    {{ Lang.get('employerdashboard::pages/employees-import/show.file_details.processed_at') }}
                </dt>

                <dd class="col-12 col-md-8 font-weight-lighter m-0">
                    <p class="m-0" v-if="isProcessed">
                        {{ formatDate(employeeImportFile.company_file?.core_processed_at) }}
                    </p>
                    <p class="m-0" v-else>
                        {{ Lang.get('employerdashboard::pages/employees-import/show.not_processed') }}
                    </p>
                </dd>
            </div>

            <div v-if="employeeImportFile.validation_errors.length > 0"
                 class="py-2 row ml-0 font-poppins color-dark-blue border-bottom">
                <dt class="col-12 col-md-4 font-weight-semibold">
                    {{ Lang.get('employerdashboard::pages/employees-import/show.file_details.validation_errors') }}
                </dt>

                <dd class="col-12 col-md-8 font-weight-lighter">
                    <div class="row mb-2 mr-1"
                         v-for="error in employeeImportFile.validation_errors">
                        <div class="col-10 col-xl-11">
                            <ul class="list-group">
                                <li class="list-group-item">
                                    <span class="font-weight-bold">
                                        {{
                                            Lang.get('employerdashboard::pages/employees-import/show.error_table.row_number')
                                        }}:
                                    </span>
                                    {{ error.row }}
                                </li>
                                <li class="list-group-item" v-for="errorMessage in error.errors">
                                    {{ errorMessage }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </dd>
            </div>

            <div v-if="employeeImportFile.company_file?.errors?.length"
                 class="py-2 row ml-0 font-poppins color-dark-blue border-bottom">
                <dt class="col-12 col-md-4 font-weight-semibold">
                    {{ Lang.get('employerdashboard::pages/employees-import/show.file_details.processing_errors') }}
                </dt>

                <dd class="col-12 col-md-8 font-weight-lighter">
                    <div class="row mb-2 mr-1"
                         v-for="error in employeeImportFile.company_file?.errors ?? []">
                        <div class="col-10 col-xl-11">
                            <ul class="list-group">
                                <li class="list-group-item">
                                    <span class="font-weight-bold">
                                        {{
                                            Lang.get('employerdashboard::pages/employees-import/show.error_table.row_number')
                                        }}:
                                    </span>
                                    {{ error.Row }}
                                </li>
                                <li class="list-group-item">
                                    {{ error.Error }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </dd>
            </div>
        </dl>
    </div>
</template>
